/**
 * The card service fee an OPERATOR is about to add, shown before they charge.
 *
 * Distinct from {@link SurchargeDisclosure}, which is the customer-facing
 * disclosure on the public pay pages. This one is for staff taking a payment
 * inside the app — over the counter, over the phone, off a saved method — where
 * the customer never sees a checkout screen and the operator is the only person
 * who can tell them what they are being charged.
 *
 * Three states, and the middle one is the point:
 *
 *  - a fee applies → the exact amount and the resulting total;
 *  - the tenant surcharges but THIS instrument cannot carry a fee (bank
 *    account, debit card) → say so explicitly. Silence reads as "the fee is
 *    coming and nobody told me", which is what operators kept reporting;
 *  - the tenant does not surcharge at all → render nothing.
 *
 * The figure is never computed here. `feeCents` comes from the server quote so
 * the amount an operator reads out is the amount the charge path adds; a null
 * fee falls back to the rate rather than inventing a number.
 */
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CardServiceFeeQuote } from '@/hooks/payments/useCardServiceFeeQuote';
import {
  effectivePaymentMethodDiscountPercent,
  formatDiscountPercent,
  surchargeToDiscountPercent,
} from '@/lib/payments/paymentMethodDiscount';

export interface ChargeServiceFeePreviewProps {
  quote?: CardServiceFeeQuote | null;
  /** Pre-fee amount being charged, for the "total" line. */
  baseAmount: number;
  /**
   * A tip charged on top of the base. The discount line then names no charge
   * total: the caller spells out base, discount and tip together, and a
   * base-only "Charge" figure beside the tipped total reads as a contradiction.
   */
  tipAmount?: number;
  currency?: string;
  className?: string;
}

function formatMoney(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

export function ChargeServiceFeePreview({
  quote,
  baseAmount,
  tipAmount = 0,
  currency = 'USD',
  className,
}: ChargeServiceFeePreviewProps) {
  if (quote?.achCheckDiscountEnabled) {
    if (!(quote.discountCents && quote.discountCents > 0)) return null;
    return (
      <p
        className={cn('text-xs text-muted-foreground', className)}
        data-testid="charge-payment-discount-preview"
      >
        ACH/check discount: {formatMoney(quote.discountCents / 100, currency)} (
        {formatDiscountPercent(quote.achCheckDiscountPercent ?? 0)}).{' '}
        {tipAmount > 0 ? null : (
          <>
            Charge{' '}
            {formatMoney(
              (quote.chargeCents ?? Math.round(baseAmount * 100) - quote.discountCents) / 100,
              currency,
            )}
            ;{' '}
          </>
        )}
        applies to {formatMoney(baseAmount, currency)} of the balance.
      </p>
    );
  }
  if (!quote?.enabled || !(quote.surchargePercent > 0)) return null;

  if (!quote.surchargeable) {
    return (
      <p
        className={cn('text-xs text-muted-foreground', className)}
        data-testid="charge-service-fee-none"
      >
        This payment method uses the ACH/check price; no card-price adjustment is added.
      </p>
    );
  }

  // An EXACT zero is an answer, not a missing one: the server priced this
  // charge and it carries no fee (a few cents rounds to nothing). Promising a
  // fee that will never be charged is the same failure as hiding one, so the
  // rate-only fallback is reserved strictly for `null` — "a fee applies, the
  // amount could not be priced".
  if (quote.feeCents === 0) return null;
  const fee = quote.feeCents == null ? null : quote.feeCents / 100;
  const discountPercent =
    fee == null
      ? surchargeToDiscountPercent(quote.surchargePercent)
      : effectivePaymentMethodDiscountPercent(baseAmount, fee, quote.surchargePercent);

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900',
        className,
      )}
      data-testid="charge-service-fee-preview"
    >
      <Info size={14} className="mt-0.5 shrink-0" />
      <div>
        {fee == null ? (
          <span className="font-medium">
            ACH/check payments receive an equivalent {formatDiscountPercent(discountPercent)}{' '}
            discount from the card price.
          </span>
        ) : (
          <>
            <span className="font-medium">
              Card price: {formatMoney(baseAmount + fee, currency)}. ACH/check price:{' '}
              {formatMoney(baseAmount, currency)} — save {formatMoney(fee, currency)} (
              {formatDiscountPercent(discountPercent)}).
            </span>{' '}
            <span className="opacity-80">
              The invoice is credited {formatMoney(baseAmount, currency)}.
            </span>
          </>
        )}
      </div>
    </div>
  );
}
